import React, { useState, useTransition } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { SearchStatus } from "../../models/Interfaces";
import { useNavigate } from "react-router";
import { immunizationsMultiMatchPdfURL, immunizationsNoMatchPdfURL } from "../../utils/data";
import { API_DOMAIN, PROTOCOL } from "../../globals";
import getIzProvider, { IzProvider } from "../../models/providers/IzProviders";
import { Modal } from "../../components/modals/Modal";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { errorLog } from "../../utils/logger";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { highContrastAtom, izSearchAPIResultAtom, selectedSearchAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { useBranding } from "../../branding/useBranding";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";

interface SearchResultsState {
  isScenarioB: boolean;
}

const jim = "/assets/images/Docket_Jim_Light.png";
const jimHC = "/assets/images/Docket_Jim_HC.png";

export function SearchResults() {
  const [existingSearch, setExistingSearch] = useAtom(selectedSearchAtom);
  const [_izSearchAPIResult, setIzSearchAPIResult] = useAtom(izSearchAPIResultAtom);
  const isWeb = Capacitor.getPlatform() === "web";

  const noMatch = existingSearch?.status === SearchStatus.noMatch;
  const multiMatch = existingSearch?.status === SearchStatus.multiMatch;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const branding = useBranding();
  const { state } = useLocation();
  const [deleteSearchModal, setDeleteSearchModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<string>();
  const [highContrast] = useAtom(highContrastAtom);
  // TODO: We should not be reading local state to consider if this is scenario B.
  // There should be a helper function that looks at the search and determines if it's scenario b
  // Scenario B is a search that has no `verified_at` time with status PARTIAL_MATCH_ALT_CONTACTS.
  const { isScenarioB } = (state as SearchResultsState) || {};

  const providerKey = existingSearch?.izProviderKey;
  let izProvider: IzProvider | undefined;
  if (providerKey) {
    izProvider = getIzProvider(providerKey, t);
  }

  const navToSearchList = () => {
    navigate("/home/search");
  };

  const navToHelpInfo = () => {
    const source =
      existingSearch?.status === SearchStatus.noMatch ||
      existingSearch?.status === SearchStatus.basicMatchNoContacts
        ? { uri: immunizationsNoMatchPdfURL, cache: true }
        : { uri: immunizationsMultiMatchPdfURL, cache: true };
    window.open(source.uri, "_blank", "noreferrer");
  };

  const handleDeleteSearchModal = async () => {
    if (isWeb) {
      setDeleteSearchModal(true);
    } else {
      const { value } = await Dialog.confirm({
        title: t("immunizations.alt_contacts_delete_heading"),
        message: t("immunizations.alt_contacts_delete_message"),
        okButtonTitle: t("generic.ok"),
        cancelButtonTitle: t("generic.cancel"),
      });
      if (value) {
        async () => {
          navToIncorrectContacts();
          await runDeleteSearch();
        };
      }
    }
  };

  const runDeleteSearch = async () => {
    if (existingSearch === null) {
      return;
    }

    try {
      await getAPIClient().deleteIzSearch(existingSearch.uid, existingSearch.izProviderKey);
      const updatedSearches = await getAPIClient().getIzSearches(false);
      await setIzSearchAPIResult(updatedSearches.data);
      await setExistingSearch(null);
      navigate("/home/search");
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  const navToIncorrectContacts = () => {
    const url = `${PROTOCOL}://${API_DOMAIN}/api/immunizations/jurisdiction/${providerKey}/incorrect_contacts`;
    window.open(url, "_blank", "noreferrer");
  };

  const isScenarioBImage = isScenarioB || noMatch || multiMatch;

  function getSearchStatusImage() {
    if (isScenarioBImage) {
      return izProvider?.scenarioBImagePath;
    }
    if (highContrast) {
      return jimHC;
    }
    return branding?.searchStatusImage || jim;
  }

  function getSearchStatusTextHeader() {
    if (isScenarioB || noMatch || multiMatch) {
      return t("immunizations.search_results_troubleshooting");
    }

    return t("immunizations.search_queued_header");
  }

  const getSearchStatusTranslationKey = () => {
    if (isScenarioB || noMatch) {
      return "immunizations.scenario_b_message";
    } else if (multiMatch) {
      return "immunizations.no_records_multi_match_message";
    }

    return "immunizations.search_queued_message";
  };

  function moreInfoCheck() {
    return multiMatch || existingSearch?.status === SearchStatus.noRecords;
  }

  function getButtonTitle() {
    if (isScenarioB || noMatch || multiMatch) {
      return t("immunizations.scenario_b_button_title", { context: providerKey });
    }
    return t("faq.faqs");
  }

  return (
    <div className="flex max-md:flex-col md:docket-gap-1 max-md:px-6 max-md:pb-20 max-md:pt-6 max-md:min-h-[calc(100vh-80px)] max-md:gap-6">
      {deleteSearchModal && (
        <Modal
          id="delete-search-modal"
          open={deleteSearchModal}
          title={t("immunizations.alt_contacts_delete_heading")}
          onCloseModal={() => setDeleteSearchModal(false)}
          hasClose={true}
          actionButtons={
            <>
              <button
                className="btn docket-button secondary"
                aria-label="cancel"
                onClick={() => setDeleteSearchModal(false)}
                data-testid="cancel-btn"
              >
                {t("generic.cancel")}
              </button>
              <button
                className="btn docket-button"
                aria-label="ok"
                onClick={async () => {
                  navToIncorrectContacts();
                  await runDeleteSearch();
                }}
              >
                {t("generic.ok")}
              </button>
            </>
          }
        >
          <p>{t("immunizations.alt_contacts_delete_message")}</p>
        </Modal>
      )}
      {showErrorModal && (
        <ErrorModal title={t("generic.error")} onCloseModal={() => setShowErrorModal(undefined)}>
          <p>{showErrorModal}</p>
        </ErrorModal>
      )}
      <div
        className={`mx-auto mt-[-50px] flex-1 md:hidden w-full max-md:dark:rounded-xl ${
          isScenarioBImage ? "dark:bg-docket-primary" : "dark:bg-dark-blue"
        }`}
      >
        <img className="w-[80%] mx-auto" src={getSearchStatusImage()} alt="Placeholder image" />
      </div>
      <div className="flex flex-col docket-gap-1 flex-1">
        <h1
          className="text-dark dark:text-white font-black text-3xl max-md:text-white"
          data-testid="search-status-header"
        >
          {getSearchStatusTextHeader()}
        </h1>
        <p
          className="text-dark dark:text-white text-md max-md:text-white max-md:mb-auto"
          data-testid="search-status-message"
        >
          <Trans i18nKey={getSearchStatusTranslationKey()} />
        </p>
        {moreInfoCheck() && (
          <button
            onClick={() => navToHelpInfo()}
            className="btn docket-button-responsive w-fit"
            data-testid="multi-match-no-match-btn"
          >
            {t("immunizations.no_match_multi_match_no_match_button")}
          </button>
        )}
        <div className="flex flex-col docket-gap-1">
          {(isScenarioB || noMatch || multiMatch) && (
            <button
              onClick={handleDeleteSearchModal}
              className="btn docket-button-responsive w-fit max-md:w-full max-md:text-lg max-md:!font-semibold max-md:!text-white max-md:!bg-dark-blue"
            >
              {getButtonTitle()}
            </button>
          )}
          {isScenarioB || noMatch || multiMatch ? (
            <button
              onClick={() => navToSearchList()}
              className="btn secondary docket-button-responsive w-fit max-md:w-full max-md:text-lg max-md:!font-semibold max-md:!text-blue-button"
              data-testid="dismiss-btn"
            >
              {t("generic.dismiss")}
            </button>
          ) : (
            <button
              onClick={() => navToSearchList()}
              className="btn docket-button-responsive w-fit max-md:w-full max-md:mb-8"
              data-testid="continue-btn"
            >
              {t("generic.continue")}
            </button>
          )}
        </div>
      </div>
      <div className="my-auto flex-1 max-md:hidden">
        <img
          className=""
          style={{ width: 300 }}
          src={getSearchStatusImage()}
          alt="Placeholder image"
          data-testid="placeholder-desktop"
        />
      </div>
    </div>
  );
}

export default SearchResults;
